/**
 * Rating utilities — single source of truth in `ratings` + `sorting_score`.
 */


/** Legacy duplicate keys at item root (removed on normalize). */
export const LEGACY_RATING_ROOT_KEYS = ['external_rating', 'ai_rating', 'average_rating'];

export function isWatchedLikeItem(item) {
  return Boolean(item?.watched_date || item?.listened_date || item?.watched_at);
}

/**
 * Merge ratings into `ratings`, drop duplicate root keys, set sorting_score.
 * @returns {{ item: object, normalized: boolean, changes: string[] }}
 */
export function normalizeRatingsShape(item, options = {}) {
  if (!item || typeof item !== 'object') {
    return { item, normalized: false, changes: [] };
  }

  const keepRootUserRating =
    options.keepRootUserRating !== undefined
      ? options.keepRootUserRating
      : isWatchedLikeItem(item);

  const changes = [];
  const ratings = { ...(item.ratings || {}) };

  const mergeKey = (key) => {
    const rootVal = item[key];
    if (rootVal == null || Number.isNaN(rootVal)) return;
    if (ratings[key] == null) {
      ratings[key] = rootVal;
      changes.push(`filled ratings.${key} from root`);
      return;
    }
    if (rootVal !== ratings[key]) {
      changes.push(`${key}: root ${rootVal} → ratings ${ratings[key]}`);
    }
  };

  mergeKey('user_rating');
  mergeKey('external_rating');
  mergeKey('ai_rating');
  mergeKey('average_rating');

  if (ratings.average_rating == null && item.sorting_score != null) {
    ratings.average_rating = item.sorting_score;
    changes.push('filled ratings.average_rating from sorting_score');
  }

  const sorting_score = ratings.average_rating ?? item.sorting_score ?? null;
  const hadLegacyAtRoot = LEGACY_RATING_ROOT_KEYS.some((k) => item[k] != null);
  const normalized = changes.length > 0 || hadLegacyAtRoot;

  const next = { ...item, ratings, sorting_score };
  LEGACY_RATING_ROOT_KEYS.forEach((k) => {
    delete next[k];
  });

  if (keepRootUserRating && ratings.user_rating != null) {
    next.user_rating = ratings.user_rating;
  } else if (!keepRootUserRating) {
    delete next.user_rating;
  }

  if (normalized) {
    next.ratings_normalized_at = new Date().toISOString();
  }

  return { item: next, normalized, changes };
}

/** Read display/sort value — always prefer `ratings`, never stale root duplicates. */
export function getRatingValue(item, key) {
  if (!item) return null;
  const v = item.ratings?.[key];
  if (v != null && !Number.isNaN(v)) return v;
  if (key === 'average_rating' && item.sorting_score != null) return item.sorting_score;
  if (LEGACY_RATING_ROOT_KEYS.includes(key)) return null;
  return item[key] ?? null;
}

export function logRatingsNormalized(item, changes, context = '') {
  const label = [context, item?.id || item?.title].filter(Boolean).join(' ');
  if (typeof console !== 'undefined' && console.warn) {
    console.warn(`[MisKosas ratings] normalized ${label}`, changes);
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('ratingsNormalized', {
        detail: { id: item?.id, title: item?.title, changes, context }
      })
    );
  }
}

/**
 * Calculates weighted average of ratings.
 */
export const calculateWeightedAverage = (ratings, weights = {
  user_rating_weight: 0.4,
  external_rating_weight: 0.3,
  ai_rating_weight: 0.3
}) => {
  const { user_rating, external_rating, ai_rating } = ratings;
  const { user_rating_weight, external_rating_weight, ai_rating_weight } = weights;

  if (!user_rating) {
    const totalWeight = external_rating_weight + ai_rating_weight;
    return (external_rating * external_rating_weight + ai_rating * ai_rating_weight) / totalWeight;
  }

  return (
    user_rating * user_rating_weight +
    external_rating * external_rating_weight +
    ai_rating * ai_rating_weight
  );
};

export const normalizeRating = (rating, source) => {
  switch (source) {
    case 'imdb':
      return rating;
    case 'rotten_tomatoes':
    case 'metacritic':
      return (rating / 100) * 10;
    case 'spotify':
    case 'apple_music':
    case 'last_fm':
      return rating;
    default:
      return rating;
  }
};

export const calculateExternalRating = (externalSources) => {
  const ratings = [];

  Object.entries(externalSources || {}).forEach(([source, data]) => {
    if (typeof data === 'number') {
      ratings.push(normalizeRating(data, source));
    } else if (data && typeof data === 'object' && data.rating) {
      ratings.push(normalizeRating(data.rating, source));
    }
  });

  return ratings.length > 0 ? ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length : 5.0;
};

export const generateAIRating = (content, userPreferences) => {
  let score = 5.0;
  const genres = userPreferences?.favorite_genres || [];

  if (content.genre && genres.length) {
    const genreList = Array.isArray(content.genre) ? content.genre : [content.genre];
    if (genreList.some((g) => genres.includes(g))) score += 1.5;
  }

  if (content.actors) {
    const favoriteActors = (content.actors || []).filter((actor) =>
      (userPreferences?.favorite_actors || []).includes(actor)
    );
    score += favoriteActors.length * 0.5;
  }

  if (content.director && userPreferences?.favorite_directors?.includes(content.director)) {
    score += 1.0;
  }

  if (content.year) {
    const age = new Date().getFullYear() - content.year;
    if (age <= 5) score += 0.5;
    else if (age <= 20) score += 0.3;
  }

  if (content.external_sources) {
    const externalRating = calculateExternalRating(content.external_sources);
    if (externalRating >= 8.0) score += 1.0;
    else if (externalRating >= 7.0) score += 0.5;
  }

  return Math.max(1, Math.min(10, Math.round(score * 10) / 10));
};

/**
 * Recalculates ratings and normalizes shape (no duplicate root keys).
 */
export const updateContentRatings = (content, userPreferences) => {
  const user_rating = content.ratings?.user_rating ?? content.user_rating;

  const ratings = {
    ...content.ratings,
    user_rating,
    external_rating: calculateExternalRating(content.external_sources || {}),
    ai_rating: generateAIRating(content, userPreferences)
  };

  ratings.average_rating = calculateWeightedAverage(
    ratings,
    userPreferences?.sorting_preferences?.weight_preferences
  );

  const merged = {
    ...content,
    ratings,
    sorting_score: ratings.average_rating
  };

  const { item, normalized, changes } = normalizeRatingsShape(merged, {
    keepRootUserRating: isWatchedLikeItem(content) || user_rating != null
  });

  if (normalized) {
    logRatingsNormalized(item, changes, 'updateContentRatings');
  }

  return item;
};

export const sortRecommendations = (recommendations, sortBy = 'average_rating') => {
  return [...recommendations].sort((a, b) => {
    let scoreA;
    let scoreB;

    switch (sortBy) {
      case 'user_rating':
        scoreA = getRatingValue(a, 'user_rating') || 0;
        scoreB = getRatingValue(b, 'user_rating') || 0;
        break;
      case 'external_rating':
        scoreA = getRatingValue(a, 'external_rating') || 0;
        scoreB = getRatingValue(b, 'external_rating') || 0;
        break;
      case 'ai_rating':
        scoreA = getRatingValue(a, 'ai_rating') || 0;
        scoreB = getRatingValue(b, 'ai_rating') || 0;
        break;
      case 'average_rating':
      default:
        scoreA = getRatingValue(a, 'average_rating') || 0;
        scoreB = getRatingValue(b, 'average_rating') || 0;
        break;
    }

    return scoreB - scoreA;
  });
};

export const formatRating = (rating, decimals = 1) => {
  if (!rating || Number.isNaN(rating)) return 'N/A';
  return Number(rating).toFixed(decimals);
};

/**
 * Map a 1–10 rating to filled/half star counts (5-star display).
 */
export function ratingToStarDisplay(rating) {
  const value = Number(rating);
  if (!value || Number.isNaN(value)) {
    return { full: 0, half: false };
  }
  const onFiveScale = Math.max(0, Math.min(10, value)) / 2;
  const full = Math.min(5, Math.floor(onFiveScale));
  const remainder = onFiveScale - full;
  const half = remainder >= 0.25 && full < 5;
  return { full, half };
}

export const getRatingColor = (rating) => {
  if (rating >= 8.5) return 'text-success';
  if (rating >= 7.0) return 'text-warning';
  if (rating >= 5.0) return 'text-info';
  return 'text-danger';
};
